export const ONBOARDING_IMPORT_DURATION_MS = 60 * 1000;

export const ONBOARDING_IMPORT_TASKS = [
  {
    id: "secure",
    labelKey: "admin.onboarding.task.secure.complete",
    activeKey: "admin.onboarding.task.secure.active",
    activityMessageKeys: ["admin.onboarding.task.secure.message"],
    label: "Secure connection established",
    activeLabel: "Securing the management system connection",
    activityMessages: ["Preparing a read-only workspace for the import"],
    completeAt: 0,
  },
  {
    id: "business",
    labelKey: "admin.onboarding.task.business.complete",
    activeKey: "admin.onboarding.task.business.active",
    activityMessageKeys: [
      "admin.onboarding.task.business.organizing",
      "admin.onboarding.task.business.checking",
      "admin.onboarding.task.business.preparing",
    ],
    label: "Business details organized",
    activeLabel: "Reading business details",
    activityMessages: [
      "Organizing clinic details, locations and opening hours",
      "Checking which business fields need your review",
      "Preparing editable business information",
    ],
    completeAt: 0.18,
  },
  {
    id: "services",
    labelKey: "admin.onboarding.task.services.complete",
    activeKey: "admin.onboarding.task.services.active",
    activityMessageKeys: [
      "admin.onboarding.task.services.pairing",
      "admin.onboarding.task.services.buffers",
      "admin.onboarding.task.services.summaries",
    ],
    label: "Services and prices imported",
    activeLabel: "Importing services and prices",
    activityMessages: [
      "Pairing each service with its current price",
      "Checking durations and booking buffers",
      "Preparing plain-language service summaries",
    ],
    completeAt: 0.36,
  },
  {
    id: "resources",
    labelKey: "admin.onboarding.task.resources.complete",
    activeKey: "admin.onboarding.task.resources.active",
    activityMessageKeys: [
      "admin.onboarding.task.resources.linking",
      "admin.onboarding.task.resources.matching",
      "admin.onboarding.task.resources.schedules",
    ],
    label: "People, rooms and hours matched",
    activeLabel: "Matching people, rooms and opening hours",
    activityMessages: [
      "Linking practitioners to the services they provide",
      "Matching rooms to booking requirements",
      "Preparing bookable schedules for review",
    ],
    completeAt: 0.55,
  },
  {
    id: "knowledge",
    labelKey: "admin.onboarding.task.knowledge.complete",
    activeKey: "admin.onboarding.task.knowledge.active",
    activityMessageKeys: [
      "admin.onboarding.task.knowledge.pages",
      "admin.onboarding.task.knowledge.organizing",
      "admin.onboarding.task.knowledge.cleaning",
    ],
    label: "Website knowledge preview prepared",
    activeLabel: "Preparing website knowledge preview",
    activityMessages: [
      "Turning public pages into reviewable business knowledge",
      "Organizing service pages, policies and FAQs",
      "Cleaning duplicate website text before review",
    ],
    completeAt: 0.74,
    requires: "website",
  },
  {
    id: "conversations",
    labelKey: "admin.onboarding.task.conversations.complete",
    activeKey: "admin.onboarding.task.conversations.active",
    activityMessageKeys: [
      "admin.onboarding.task.conversations.matching",
      "admin.onboarding.task.conversations.finding",
      "admin.onboarding.task.conversations.value",
    ],
    label: "Customer conversations prepared",
    activeLabel: "Loading recent customer conversations",
    activityMessages: [
      "Matching recent threads to customer records",
      "Finding the service behind each open conversation",
      "Preparing opportunity value for the live Inbox",
    ],
    completeAt: 0.91,
    requires: "channel",
  },
];

export function getOnboardingImportSnapshot(onboarding, now = Date.now()) {
  if (!onboarding?.importStartedAt) {
    return {
      running: false,
      ready: Boolean(onboarding?.businessDataReadyAt),
      completedCount: 0,
      completedTaskIds: [],
      progress: 0,
      activeTask: null,
      activeMessage: null,
      blockedBy: null,
      latestCompletedTask: null,
    };
  }

  if (onboarding.businessDataReadyAt) {
    return {
      running: false,
      ready: true,
      completedCount: ONBOARDING_IMPORT_TASKS.length,
      completedTaskIds: ONBOARDING_IMPORT_TASKS.map((task) => task.id),
      progress: 100,
      activeTask: null,
      activeMessage: null,
      blockedBy: null,
      latestCompletedTask: ONBOARDING_IMPORT_TASKS.at(-1),
    };
  }

  const startedAt = new Date(onboarding.importStartedAt).getTime();
  const ratio = Math.max(0, Math.min(1, (now - startedAt) / ONBOARDING_IMPORT_DURATION_MS));
  const requirementMet = (task) => {
    if (task.requires === "website") return Boolean(onboarding.websiteUrl);
    if (task.requires === "channel") return Boolean(onboarding.connectedChannelIds?.length);
    return true;
  };
  const completedTaskIds = ONBOARDING_IMPORT_TASKS
    .filter((task) => ratio >= task.completeAt && requirementMet(task))
    .map((task) => task.id);
  const completedCount = completedTaskIds.length;
  const activeTask = ONBOARDING_IMPORT_TASKS.find((task) => ratio < task.completeAt || !requirementMet(task)) || null;
  const allTasksReady = completedCount === ONBOARDING_IMPORT_TASKS.length;
  const blockedBy = activeTask?.requires && !requirementMet(activeTask) ? activeTask.requires : null;
  const activeTaskIndex = ONBOARDING_IMPORT_TASKS.findIndex((task) => task.id === activeTask?.id);
  const previousCompleteAt = activeTaskIndex > 0 ? ONBOARDING_IMPORT_TASKS[activeTaskIndex - 1].completeAt : 0;
  const activeStartedAt = startedAt + previousCompleteAt * ONBOARDING_IMPORT_DURATION_MS;
  const activityMessages = activeTask?.activityMessages || [];
  const activityMessageKeys = activeTask?.activityMessageKeys || [];
  const activeMessageIndex = activityMessages.length
    ? Math.floor(Math.max(0, now - activeStartedAt) / 4800) % activityMessages.length
    : 0;
  const latestCompletedTask = ONBOARDING_IMPORT_TASKS
    .filter((task) => completedTaskIds.includes(task.id))
    .at(-1) || null;

  return {
    running: !allTasksReady,
    ready: allTasksReady,
    completedCount,
    completedTaskIds,
    progress: Math.round((completedCount / ONBOARDING_IMPORT_TASKS.length) * 100),
    activeTask: allTasksReady ? null : activeTask,
    activeMessage: blockedBy || allTasksReady ? null : activityMessages[activeMessageIndex],
    activeMessageKey: blockedBy || allTasksReady ? null : activityMessageKeys[activeMessageIndex],
    blockedBy: allTasksReady ? null : blockedBy,
    latestCompletedTask,
  };
}
